import { asJSONSafe } from "./index";
import { normalizeIndicators } from "./normalization";

const parseNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Группы по лаборатории + группе услуг, вложенные показатели — как в постановлении (JSONB на плане).
 */
export function serializePlanIndicesToIndicatorGroups(planIndices) {
  const active = (planIndices || []).filter((r) => !r._isDeleted);
  const map = new Map();
  for (const row of active) {
    const hasDept =
      row.departmentId != null && String(row.departmentId).trim() !== "";
    const hasGroup =
      row.groupId != null && String(row.groupId).trim() !== "";
    const dk = hasDept ? String(row.departmentId) : "_";
    const gk = hasGroup ? String(row.groupId) : "_";
    const key = `${dk}|${gk}`;
    if (!map.has(key)) {
      map.set(key, {
        departmentId: hasDept ? Number(row.departmentId) : null,
        departmentName: row.departmentName || null,
        groupId: hasGroup ? Number(row.groupId) : null,
        groupName: row.groupName || null,
        indicators: [],
      });
    }
    map.get(key).indicators.push({
      indexId:
        row.indexId === "" || row.indexId == null
          ? null
          : Number(row.indexId),
      indexName: row.indexName || "",
      subGroupId:
        row.subGroupId != null && row.subGroupId !== ""
          ? Number(row.subGroupId)
          : null,
      serviceId:
        row.serviceId != null && row.serviceId !== ""
          ? Number(row.serviceId)
          : null,
      plannedPlan: parseNum(row.plannedPlan),
      plannedResearch: parseNum(row.plannedResearch),
      plannedSum: parseNum(row.plannedSum),
      donePlan: parseNum(row.donePlan),
      doneResearch: parseNum(row.doneResearch),
      doneSum: parseNum(row.doneSum),
      checked: true,
    });
  }
  return Array.from(map.values());
}

function parseIndicatorsRaw(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const once = asJSONSafe(trimmed, null);
    // Частый кейс: JSONB пришёл как строка, внутри которой ещё одна JSON-строка:
    // "\"[{...}]\"" -> после первого parse получаем строку "[{...}]", нужно распарсить ещё раз.
    if (typeof once === "string") {
      const inner = once.trim();
      if (!inner) return null;
      return asJSONSafe(inner, null);
    }
    return once;
  }
  return raw;
}

function isGroupedShape(data) {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === "object" &&
    data[0] !== null &&
    Array.isArray(data[0].indicators ?? data[0].Indicators)
  );
}

function isFlatPlanRow(row) {
  return (
    row &&
    typeof row === "object" &&
    ("indexId" in row ||
      "indexName" in row ||
      "plannedPlan" in row ||
      "IndexId" in row)
  );
}

/**
 * Строки таблицы индексов плана (форма create/edit).
 */
export function deserializePlanIndicatorsToPlanIndices(raw) {
  const data = parseIndicatorsRaw(raw);
  if (!data) return [];

  if (isGroupedShape(data)) {
    const rows = [];
    for (const g of data) {
      const deptId = g.departmentId ?? g.DepartmentId ?? null;
      const deptName = g.departmentName ?? g.DepartmentName ?? null;
      const grpId = g.groupId ?? g.GroupId ?? null;
      const grpName = g.groupName ?? g.GroupName ?? null;
      const items = g.indicators ?? g.Indicators ?? [];
      for (const ind of items) {
        const i = ind || {};
        rows.push({
          id: null,
          indexId:
            i.indexId != null && i.indexId !== ""
              ? String(i.indexId)
              : i.IndexId != null && i.IndexId !== ""
                ? String(i.IndexId)
                : "",
          indexName:
            i.indexName ?? i.IndexName ?? i.name ?? i.Name ?? "",
          subGroupId:
            i.subGroupId ?? i.SubGroupId ?? i.subGroupID ?? i.SubGroupID ?? null,
          serviceId: i.serviceId ?? i.ServiceId ?? null,
          departmentId: deptId,
          departmentName: deptName,
          groupId: grpId ?? i.groupId ?? i.GroupId ?? null,
          groupName: grpName ?? i.groupName ?? i.GroupName ?? null,
          plannedPlan: parseNum(i.plannedPlan ?? i.PlannedPlan),
          plannedResearch: parseNum(i.plannedResearch ?? i.PlannedResearch),
          plannedSum: parseNum(i.plannedSum ?? i.PlannedSum),
          donePlan: parseNum(i.donePlan ?? i.DonePlan),
          doneResearch: parseNum(i.doneResearch ?? i.DoneResearch),
          doneSum: parseNum(i.doneSum ?? i.DoneSum),
          _isDeleted: false,
          _manualSum: false,
        });
      }
    }
    return rows;
  }

  if (Array.isArray(data) && data.length > 0 && isFlatPlanRow(data[0])) {
    return data
      .filter((r) => r && !r._isDeleted)
      .map((row) => ({
        id: row.id ?? null,
        indexId:
          row.indexId != null && row.indexId !== ""
            ? String(row.indexId)
            : row.IndexId != null && row.IndexId !== ""
              ? String(row.IndexId)
              : "",
        indexName:
          row.indexName ?? row.IndexName ?? row.name ?? row.Name ?? "",
        subGroupId:
          row.subGroupId ?? row.SubGroupId ?? row.subGroupID ?? row.SubGroupID ?? null,
        serviceId: row.serviceId ?? row.ServiceId ?? null,
        departmentId: row.departmentId ?? row.DepartmentId ?? null,
        departmentName: row.departmentName ?? row.DepartmentName ?? null,
        groupId: row.groupId ?? row.GroupId ?? null,
        groupName: row.groupName ?? row.GroupName ?? null,
        plannedPlan: parseNum(row.plannedPlan ?? row.PlannedPlan),
        plannedResearch: parseNum(row.plannedResearch ?? row.PlannedResearch),
        plannedSum: parseNum(row.plannedSum ?? row.PlannedSum),
        donePlan: parseNum(row.donePlan ?? row.DonePlan),
        doneResearch: parseNum(row.doneResearch ?? row.DoneResearch),
        doneSum: parseNum(row.doneSum ?? row.DoneSum),
        _isDeleted: false,
        _manualSum: row._manualSum ?? false,
      }));
  }

  return [];
}

const SYNTHETIC_PLAN_TO_RESOLUTION_ID_BASE = 1900000000;

/**
 * Группы для формы постановления (RHFGroupIndicator): departmentId, groupId, indicators[{ id, name, groupId, checked }].
 * Берётся из плана (JSONB indicators), как после deserializePlanIndicatorsToPlanIndices.
 */
export function buildResolutionIndicatorsFromPlanEntity(plan) {
  const flat = deserializePlanIndicatorsToPlanIndices(
    plan?.indicators ?? plan?.Indicators
  );
  if (!flat.length) return null;

  const map = new Map();
  let synth = 0;

  for (const row of flat) {
    if (row._isDeleted) continue;
    const hasDept =
      row.departmentId != null && String(row.departmentId).trim() !== "";
    const hasGroup =
      row.groupId != null && String(row.groupId).trim() !== "";
    const dk = hasDept ? String(row.departmentId) : "_";
    const gk = hasGroup ? String(row.groupId) : "_";
    const key = `${dk}|${gk}`;
    if (!map.has(key)) {
      map.set(key, {
        departmentId: hasDept ? Number(row.departmentId) : null,
        groupId: hasGroup ? Number(row.groupId) : null,
        indicators: [],
      });
    }
    const gRow = map.get(key);
    const idNum =
      row.indexId !== "" &&
      row.indexId != null &&
      Number.isFinite(Number(row.indexId))
        ? Number(row.indexId)
        : SYNTHETIC_PLAN_TO_RESOLUTION_ID_BASE + (synth += 1);
    gRow.indicators.push({
      id: idNum,
      name:
        row.indexName ||
        (Number.isFinite(Number(row.indexId))
          ? `Индекс ${row.indexId}`
          : "Показатель"),
      groupId: hasGroup ? Number(row.groupId) : gRow.groupId,
      checked: true,
    });
  }

  const groups = Array.from(map.values()).filter((g) => g.indicators.length);
  if (!groups.length) return null;
  return normalizeIndicators(groups);
}
